import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DisclosureToggle } from '@/components/Disclosure';
import { VendorMark } from '@/components/VendorMark';
import { cn } from '@/lib/utils';
import { fmtDate } from '@/features/orders/lib';
import {
  CALENDAR_KINDS, KIND_LABELS, PAST_DUE_LABELS, countText, relativeDay,
  type CalendarItem, type CalendarKind,
} from './calendarItems';
import { KIND_ICONS, KIND_TONE } from './kindStyle';

/**
 * Everything that has passed its date, however long ago — the calendar only
 * reaches back a week. Its own panel, unfiltered: one column per kind, so a
 * late order deadline never sits in a list of late parcels.
 */

const OPEN_KEY = 'orders.overview.pastDueOpen';
/** Rows a kind shows before the rest fold; one kind alone flows into columns. */
const GROUP_ROWS = 5;
const SOLO_ROWS = 15;

function readOpen(): boolean {
  try { return localStorage.getItem(OPEN_KEY) !== '0'; } catch { return true; }
}

function PastDueRow({ item, today, returnTo }: { item: CalendarItem; today: string; returnTo: string }) {
  return (
    <Link
      to={item.to}
      state={{ returnTo }}
      className="flex break-inside-avoid items-center gap-3 rounded-[6px] px-2 py-1.5 hover:bg-accent"
    >
      <span className="flex w-[72px] shrink-0 justify-center"><VendorMark name={item.vendor} logoUrl={item.logoUrl} /></span>
      <span className="min-w-0 flex-1">
        <strong className="block truncate text-[13px] leading-[1.3] font-semibold">{item.vendor}</strong>
        <span className="block truncate text-[12px] leading-[1.4] text-muted-foreground">
          {[fmtDate(item.day), item.label, countText(item), item.fact].filter(Boolean).join(' · ')}
        </span>
      </span>
      <span className="shrink-0 text-[12px] font-semibold whitespace-nowrap text-danger-ink">{relativeDay(item.day, today)}</span>
    </Link>
  );
}

function PastDueGroup({ kind, items, solo, today, returnTo }: {
  kind: CalendarKind; items: CalendarItem[]; solo: boolean; today: string; returnTo: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const cap = solo ? SOLO_ROWS : GROUP_ROWS;
  // Folding a single row behind "1 more" hides one to show one.
  const folds = items.length > cap + 1;
  const visible = folds && !expanded ? items.slice(0, cap) : items;
  const Icon = KIND_ICONS[kind];
  return (
    <div className="min-w-0">
      <h3 className={cn('mb-1 flex items-center gap-1.5 px-2 text-[12px] font-semibold text-(--chip-ink)', KIND_TONE[kind])}>
        <Icon aria-hidden="true" className="size-3.5 shrink-0" />
        {PAST_DUE_LABELS[kind] ?? KIND_LABELS[kind]}
        <span className="text-muted-foreground tabular-nums">{items.length}</span>
      </h3>
      <div className={cn(solo && '[column-width:320px] gap-x-6')}>
        {visible.map((item) => <PastDueRow key={item.key} item={item} today={today} returnTo={returnTo} />)}
      </div>
      {folds && (
        <DisclosureToggle open={expanded} onToggle={() => setExpanded((v) => !v)} className="mt-1 px-2">
          {expanded ? 'Show fewer' : `${items.length - cap} more`}
        </DisclosureToggle>
      )}
    </div>
  );
}

export function PastDue({ items, today }: { items: CalendarItem[]; today: string }) {
  const location = useLocation();
  const returnTo = `/${location.search}`;
  const [open, setOpen] = useState(readOpen);
  const toggle = () => setOpen((prev) => {
    try { localStorage.setItem(OPEN_KEY, prev ? '0' : '1'); } catch { /* per-viewer convenience only */ }
    return !prev;
  });
  // Oldest first inside a kind: the longest-late thing leads.
  const groups = CALENDAR_KINDS
    .map((kind) => ({ kind, items: items.filter((i) => i.kind === kind) }))
    .filter((g) => g.items.length > 0);
  const solo = groups.length === 1;
  const shown = open && items.length > 0;

  return (
    <div className="px-4 py-3">
      <h2>
        <DisclosureToggle open={shown} onToggle={toggle} className="font-display text-[15px] font-bold text-foreground">
          <span className="size-2 shrink-0 rounded-full bg-danger data-[none]:bg-success" data-none={items.length === 0 || undefined} />
          Past due
          <span className="tabular-nums text-muted-foreground">{items.length}</span>
        </DisclosureToggle>
      </h2>
      {shown && (
        <div className={cn(
          'mt-2.5 grid items-start gap-x-6 gap-y-3',
          !solo && 'grid-cols-[repeat(auto-fit,minmax(min(100%,320px),1fr))]'
        )}>
          {groups.map((g) => (
            <PastDueGroup key={g.kind} kind={g.kind} items={g.items} solo={solo} today={today} returnTo={returnTo} />
          ))}
        </div>
      )}
    </div>
  );
}
